import { datadogRum } from "@datadog/browser-rum";
import { getOriginalResource } from "PFApp/booking/parts/helpers";
import { useReassignActionApi } from "PFApp/booking/parts/overview/booking_module_calendar/context/reassign_action_context";
import { useBookingModuleStoreActions, useCalendarRoot } from "PFApp/booking/store";
import { Row } from "PFApp/booking/types";
import { ProfileBasic } from "PFApp/booking/types/row";
import { isProjects } from "PFApp/booking/utils/calendar_root.utils";
import { ProjectItemActivity } from "PFCore/hooks/queries/bookings/projects/use_projects_entries";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { useIsUnbiasedMode } from "PFCore/hooks/use_is_unbiased_mode";
import { useTranslation } from "react-i18next";

import { DnDMode } from "../../../bars/booking_bar/use_booking_bar_dnd";
import { BookingDragAndDropData } from "../booking_drag_and_drop_manager.types";
import { getBookingDatesDiff } from "../drag_calculations";
import { findClosestProfileRow } from "../use_booking_drop_zone_manager/find_closest_profile_row";
import { useIsAuditBooking } from "../use_is_audit_booking";
import { getSuspendedProfileDetails } from "./get_suspended_profile_details";
import { useUpdateBookingDates } from "./use_update_booking_dates";

const DROP_ACTION_BY_MODE = {
  [DnDMode.Horizontal]: "dragAndDropBooking.bookingMove",
  [DnDMode.HorizontalVertical]: "dragAndDropBooking.bookingMove",
  [DnDMode.StartEdge]: "dragAndDropBooking.bookingChangeStart",
  [DnDMode.EndEdge]: "dragAndDropBooking.bookingChangeEnd"
} as const;

type UseBookingDropManager = {
  onDragEnd: () => void;
  onDragCancel: () => void;
};

type UseBookingDropManagerReturn = {
  onDrop: (dragData: BookingDragAndDropData) => Promise<void>;
};

export const useBookingDropManager = ({
  onDragEnd,
  onDragCancel
}: UseBookingDropManager): UseBookingDropManagerReturn => {
  const { t } = useTranslation("bookingModule");
  const calendarRoot = useCalendarRoot();
  const { getBookingData, getCurrentRowsByKey } = useBookingModuleStoreActions();
  const { openModal: openReassignModal } = useReassignActionApi();
  const { formatDatabaseDateTime } = useDateFormatter();
  const isUnbiasedMode = useIsUnbiasedMode();
  const updateBookingDates = useUpdateBookingDates();
  const isAuditBooking = useIsAuditBooking();

  const isUnbiasedProfileRow = (profileRow: Row, rowsByKey: Record<string, Row>): boolean => {
    const parentRow = rowsByKey[profileRow.parents[0]];
    const parentActivity = parentRow && (getOriginalResource(parentRow, calendarRoot) as ProjectItemActivity);
    return isUnbiasedMode(parentActivity?.reviewers);
  };

  const finishDrop = (isSaved: boolean): void => (isSaved ? onDragEnd() : onDragCancel());

  const onDrop = async ({
    bookingId,
    draggingMode,
    draggingStartDate,
    draggingEndDate,
    sourceRow,
    targetRow
  }: BookingDragAndDropData): Promise<void> => {
    const booking = bookingId ? getBookingData(bookingId)?.booking : undefined;
    if (!booking || !draggingMode || !draggingStartDate || !draggingEndDate || !sourceRow) {
      onDragCancel();
      return;
    }

    datadogRum.addAction(t(DROP_ACTION_BY_MODE[draggingMode], { lng: "en" }));

    const hasNewDates = getBookingDatesDiff(booking, draggingStartDate, draggingEndDate) !== 0;
    if (isAuditBooking(booking) && hasNewDates && targetRow) {
      onDragCancel();
      return;
    }

    const saveNewDates = () =>
      updateBookingDates(booking, {
        startDate: formatDatabaseDateTime(new Date(draggingStartDate).getTime()),
        endDate: formatDatabaseDateTime(new Date(draggingEndDate).getTime())
      });

    if (draggingMode === DnDMode.HorizontalVertical && targetRow) {
      const rowsByKey = getCurrentRowsByKey();
      const sourceProfileRow = findClosestProfileRow({ row: sourceRow, rowsByKey });
      if (!sourceProfileRow) {
        onDragCancel();
        return;
      }

      const sourceProfile = getOriginalResource(sourceProfileRow, calendarRoot) as ProfileBasic;
      const targetProfile = getOriginalResource(targetRow, calendarRoot) as ProfileBasic;
      openReassignModal({
        activityName: booking.activity?.name,
        sourceProfileId: Number(sourceProfile.id),
        sourceProfileIsUnbiasedMode: isUnbiasedProfileRow(sourceProfileRow, rowsByKey),
        targetProfileId: Number(targetProfile.id),
        targetProfileIsUnbiasedMode: isUnbiasedProfileRow(targetRow, rowsByKey),
        suspendedProfileDetails: isProjects(calendarRoot)
          ? getSuspendedProfileDetails(sourceProfile)
          : undefined,
        bookings: [booking],
        onClose: onDragCancel,
        onError: onDragCancel,
        onSubmit: async () => finishDrop(!hasNewDates || (await saveNewDates()))
      });
      return;
    }

    if (!hasNewDates) {
      onDragCancel();
      return;
    }

    finishDrop(await saveNewDates());
  };

  return { onDrop };
};
